import { createRouter, createRoute, createRootRouteWithContext, redirect, notFound } from '@tanstack/react-router';
import { Suspense } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { RootLayout } from '@/layouts/RootLayout';
import { HowToPage } from '@/pages/HowToPage';
import { SimulationsPage } from '@/pages/SimulationsPage';
import { ConfigPage } from '@/pages/ConfigPage';
import { ResultsPage } from '@/pages/ResultsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LoadingPage } from '@/pages/LoadingPage';
import { LoginPage } from '@/pages/LoginPage';
import { SetupPage } from '@/pages/SetupPage';
import { UsersPage } from '@/features/admin';
import { LicenseAdminPage } from '@/features/license';
import { DatasetsPage, SourcesAdminPage } from '@/features/tracker';
import { AccountPage } from '@/pages/AccountPage';
import { simulationQueryOptions } from '@/lib/queryClient';
import { ApiError } from '@/lib/api';
import { STATUS_FILTER_VALUES } from '@/components/molecules';
import { getStoredToken, getStoredUser } from '@/features/auth/utils/authStorage';
import { TanStackRouterDevtools } from './router.devtools';

/**
 * Check if user is authenticated. If not, redirect to login.
 */
function requireAuth() {
  const token = getStoredToken();
  if (!token) {
    throw redirect({ to: '/login' });
  }
}

/**
 * Check if user is authenticated and is an admin. If not, redirect appropriately.
 */
function requireAdmin() {
  const token = getStoredToken();
  if (!token) {
    throw redirect({ to: '/login' });
  }
  const user = getStoredUser();
  if (!user || !user.is_admin) {
    throw redirect({ to: '/' });
  }
}

// Search params schema for simulation pages (config + results)
const resultsSearchSchema = z.object({
  // Results page params
  view: z.enum(['timeline', 'cards']).optional().default('timeline'),
  groupMode: z.enum(['team', 'feature']).optional().default('team'),
  teams: z.array(z.string()).optional(),
  features: z.array(z.string()).optional(),
  status: z.array(z.enum(STATUS_FILTER_VALUES)).optional(),
  focus: z.string().optional(), // Deliverable UUID — scroll to and highlight in config view
  // Config page params
  activeTeam: z.number().optional(), // Index of active team tab
});

export type ResultsSearchParams = z.infer<typeof resultsSearchSchema>;

// Router context type - gives routes access to queryClient
interface RouterContext {
  queryClient: QueryClient;
}

// Root route - provides layout wrapper with devtools
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <RootLayout />
      <Suspense>
        <TanStackRouterDevtools position="bottom-right" />
      </Suspense>
    </>
  ),
});

// How To page (home)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HowToPage,
});

// Login page
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// Setup page (first-time setup wizard)
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  component: SetupPage,
});

// Simulations list page (protected)
const simulationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/simulations',
  beforeLoad: requireAuth,
  component: SimulationsPage,
});

// Simulation config page (protected, shares search params with results for seamless navigation)
const simulationConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/simulation/$simulationId',
  validateSearch: resultsSearchSchema,
  beforeLoad: requireAuth,
  loader: async ({ params, context: { queryClient } }) => {
    try {
      const data = await queryClient.ensureQueryData(simulationQueryOptions(params.simulationId));
      return { simulation: data };
    } catch (error) {
      // Treat both "not found" (404) and "invalid ID" (400) as not found
      if (error instanceof ApiError && (error.statusCode === 404 || error.statusCode === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  pendingComponent: LoadingPage,
  notFoundComponent: () => (
    <NotFoundPage message="The simulation you're looking for doesn't exist or has an invalid ID." />
  ),
  component: ConfigPage,
});

// Simulation results page (protected)
const simulationResultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/simulation/$simulationId/results',
  validateSearch: resultsSearchSchema,
  beforeLoad: requireAuth,
  loader: async ({ params, context: { queryClient } }) => {
    try {
      const data = await queryClient.ensureQueryData(simulationQueryOptions(params.simulationId));
      return { simulation: data };
    } catch (error) {
      // Treat both "not found" (404) and "invalid ID" (400) as not found
      if (error instanceof ApiError && (error.statusCode === 404 || error.statusCode === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  pendingComponent: LoadingPage,
  notFoundComponent: () => (
    <NotFoundPage message="The simulation you're looking for doesn't exist or has an invalid ID." />
  ),
  component: ResultsPage,
});

// Account settings page (protected)
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  beforeLoad: requireAuth,
  component: AccountPage,
});

// Admin users page (admin only)
const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  beforeLoad: requireAdmin,
  component: UsersPage,
});

// Admin license page (admin only)
const adminLicenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/license',
  beforeLoad: requireAdmin,
  component: LicenseAdminPage,
});

// Admin tracker-sources page (admin only)
const adminTrackerSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tracker-sources',
  beforeLoad: requireAdmin,
  component: SourcesAdminPage,
});

// Datasets page (any authenticated user can view; ownership gates edit)
const datasetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/datasets',
  beforeLoad: requireAuth,
  component: DatasetsPage,
});

// Catch-all route that redirects to home
const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});

// Route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  setupRoute,
  simulationsRoute,
  simulationConfigRoute,
  simulationResultsRoute,
  accountRoute,
  adminUsersRoute,
  adminLicenseRoute,
  adminTrackerSourcesRoute,
  datasetsRoute,
  catchAllRoute,
]);

// Create router instance
export const router = createRouter({
  routeTree,
  context: {
    queryClient: undefined!, // Will be set in main.tsx
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

// Type declaration for router - enables type-safe navigation
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
